import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Pencil, Trash2, ChevronUp, ChevronDown, Layers } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const PHASE_COLORS = {
  launch: 'bg-green-100 text-green-800',
  phase_2: 'bg-blue-100 text-blue-800',
};

const AdminCategoriesPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/categories`);
      setCategories(res.data);
    } catch {
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.role !== 'admin') { navigate('/dashboard'); return; }
    fetchCategories();
  }, [user, navigate, fetchCategories]);

  const toggleActive = async (cat) => {
    try {
      await axios.put(`${API}/admin/categories/${cat.id}`, { is_active: !cat.is_active });
      toast.success(`${cat.name?.en || cat.id} ${!cat.is_active ? 'activated' : 'deactivated'}`);
      fetchCategories();
    } catch {
      toast.error('Failed to update category');
    }
  };

  const deleteCategory = async (cat) => {
    if (!window.confirm(`Delete "${cat.name?.en || cat.id}"? This will also delete all its subcategories.`)) return;
    try {
      await axios.delete(`${API}/admin/categories/${cat.id}`);
      toast.success('Category deleted');
      fetchCategories();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete category');
    }
  };

  const move = async (index, direction) => {
    const next = [...categories];
    const swapIdx = index + direction;
    if (swapIdx < 0 || swapIdx >= next.length) return;
    [next[index], next[swapIdx]] = [next[swapIdx], next[index]];
    setCategories(next);
    try {
      await axios.put(`${API}/admin/categories/reorder`, { ordered_ids: next.map(c => c.id) });
    } catch {
      toast.error('Failed to save order');
      fetchCategories();
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;

  return (
    <div className="min-h-screen bg-background">
      <nav className="bg-white border-b">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/admin-management')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Admin
          </Button>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit' }}>
            Categories
          </h1>
          <div className="ml-auto">
            <Button onClick={() => navigate('/admin/categories/new')}>
              <Plus className="w-4 h-4 mr-2" />
              Add Category
            </Button>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-8 max-w-5xl">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Layers className="w-5 h-5" />
              All Categories ({categories.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {categories.length === 0 ? (
              <div className="py-12 text-center text-muted-foreground">No categories yet.</div>
            ) : (
              <div className="divide-y">
                {categories.map((cat, idx) => (
                  <div key={cat.id} className="flex items-center gap-4 px-6 py-4 hover:bg-gray-50">
                    {/* Reorder */}
                    <div className="flex flex-col gap-0.5">
                      <button
                        onClick={() => move(idx, -1)}
                        disabled={idx === 0}
                        className="p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-30"
                      >
                        <ChevronUp className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => move(idx, 1)}
                        disabled={idx === categories.length - 1}
                        className="p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-30"
                      >
                        <ChevronDown className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold">{cat.name?.en || cat.id}</span>
                        <span className="text-xs text-muted-foreground font-mono">{cat.slug || cat.id}</span>
                        {cat.launch_phase && (
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${PHASE_COLORS[cat.launch_phase] || 'bg-gray-100'}`}>
                            {cat.launch_phase}
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground mt-0.5">
                        {cat.subcategory_count ?? '—'} subcategories
                        {cat.icon_slug && <span className="ml-3">icon: {cat.icon_slug}</span>}
                      </div>
                    </div>

                    {/* Active toggle */}
                    <Badge
                      className="cursor-pointer select-none"
                      variant={cat.is_active !== false ? 'default' : 'outline'}
                      onClick={() => toggleActive(cat)}
                    >
                      {cat.is_active !== false ? 'Active' : 'Inactive'}
                    </Badge>

                    {/* Actions */}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => navigate(`/admin/categories/${cat.id}`)}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => deleteCategory(cat)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminCategoriesPage;
